import { normalizePartnerLayout } from './layoutTreeUtils';

const ORIGIN_WINDOW_MS = 30 * 60 * 1000;
const PLAN_OPEN_EVENTS = new Set(['webview_opened', 'editor_opened']);
const OBJECT_ID_RE = /^[0-9a-fA-F]{24}$/;

const buildGalleryUrlFromPlan = (plan) => {
  if (!plan?._id) return null;
  if (!OBJECT_ID_RE.test(String(plan._id))) return null;
  return `${window.location.origin}/floor-gallery/${plan._id}`;
};

const galleryUrlFromRow = (row) => {
  const detail = row?.detail || {};
  const meta = row?.meta || {};
  const url =
    (typeof detail.galleryUrl === 'string' && detail.galleryUrl) ||
    (typeof meta.galleryUrl === 'string' && meta.galleryUrl) ||
    null;
  if (url) return url;
  const id =
    (typeof detail.galleryId === 'string' && detail.galleryId) ||
    (typeof meta.galleryId === 'string' && meta.galleryId) ||
    null;
  if (id && OBJECT_ID_RE.test(id)) {
    return `${window.location.origin}/floor-gallery/${id}`;
  }
  return null;
};

const pickString = (...vals) => {
  for (const v of vals) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return null;
};

const buildCatalogIndex = (catalogLayouts) => {
  const byUuid = new Map();
  const byUniqueId = new Map();
  (Array.isArray(catalogLayouts) ? catalogLayouts : []).forEach((raw) => {
    const plan = normalizePartnerLayout(raw);
    if (!plan) return;
    if (plan.uuid) byUuid.set(String(plan.uuid), plan);
    if (plan.uniqueId) byUniqueId.set(String(plan.uniqueId), plan);
  });
  return { byUuid, byUniqueId };
};

const planFromRowPayload = (row, index) => {
  const meta = row?.meta || {};
  const detail = row?.detail || {};
  const layoutUuid = pickString(
    meta.layoutUuid,
    detail.layoutUuid,
    meta.layout_uuid,
    detail.layout_uuid
  );
  const uniqueId = pickString(meta.uniqueId, detail.uniqueId, meta.unique_id, detail.unique_id);
  if (layoutUuid && index.byUuid.has(layoutUuid)) {
    return { plan: index.byUuid.get(layoutUuid), layoutUuid, uniqueId };
  }
  if (uniqueId && index.byUniqueId.has(uniqueId)) {
    return { plan: index.byUniqueId.get(uniqueId), layoutUuid: null, uniqueId };
  }
  return null;
};

const sessionKeyFromRow = (row) => {
  const meta = row?.meta || {};
  return (
    meta.guestSessionId ||
    (meta.user && meta.user !== 'guest' && meta.user !== 'anonymous' ? meta.user : null)
  );
};

/**
 * Resolve which floor plan (if any) a sign-up is tied to.
 *
 * @returns {{
 *   kind: 'floor_plan' | 'signup_page' | 'unknown',
 *   plan?: { name, thumbnail, uniqueId, uuid },
 *   label: string,
 * }}
 */
export const resolveSignupFloorPlanOrigin = (row, allRows, catalogLayouts) => {
  const index = buildCatalogIndex(catalogLayouts);

  const direct = planFromRowPayload(row, index);
  if (direct?.plan) {
    return {
      kind: 'floor_plan',
      plan: direct.plan,
      label: direct.plan.name || 'Floor plan',
      galleryUrl: buildGalleryUrlFromPlan(direct.plan) || galleryUrlFromRow(row),
    };
  }

  const signupMs = new Date(row?.timestamp || 0).getTime();
  const sessionKey = sessionKeyFromRow(row);

  if (Number.isFinite(signupMs) && sessionKey && Array.isArray(allRows)) {
    let best = null;
    let bestDelta = Infinity;

    allRows.forEach((candidate) => {
      if (!PLAN_OPEN_EVENTS.has(candidate?.meta?.event)) return;
      if (sessionKeyFromRow(candidate) !== sessionKey) return;
      const ts = new Date(candidate.timestamp || 0).getTime();
      if (!Number.isFinite(ts) || ts > signupMs) return;
      const delta = signupMs - ts;
      if (delta > ORIGIN_WINDOW_MS || delta >= bestDelta) return;
      const hit = planFromRowPayload(candidate, index);
      if (!hit?.plan) return;
      best = hit;
      bestDelta = delta;
    });

    if (best?.plan) {
      return {
        kind: 'floor_plan',
        plan: best.plan,
        label: best.plan.name || 'Floor plan',
        galleryUrl: buildGalleryUrlFromPlan(best.plan) || galleryUrlFromRow(row),
      };
    }
  }

  const fallbackGalleryUrl = galleryUrlFromRow(row);
  if (fallbackGalleryUrl) {
    return {
      kind: 'floor_plan',
      plan: null,
      label: fallbackGalleryUrl,
      galleryUrl: fallbackGalleryUrl,
    };
  }

  // Standalone /signup origin. The label and galleryUrl both point at the
  // partner's /signup page; the consuming row renders the URL as a clickable
  // indigo link (same chrome as the floor_plan branch, without the avatar).
  // window is always defined here because resolveSignupFloorPlanOrigin is
  // called from a React component at render time, not SSR.
  return {
    kind: 'signup_page',
    label: `${window.location.origin}/signup`,
    galleryUrl: `${window.location.origin}/signup`,
  };
};

/** Canonical phrase — use everywhere client copies are mentioned. */
export const CLIENT_COPY_LISTING_PHRASE =
  "Client copies share the parent's listing.";
